/**
 * KeypadButton Component
 * Individual button for the in-call keypad with digit and optional sub-label
 */
import type { KeypadButtonProps } from './index.types';
import React from 'react';
import { cn } from '~/lib/utils';
import { usePressActive } from './hooks/usePressActive';

export const KeypadButton = React.memo(
  ({
    digit,
    label = '',
    onClick,
    testID,
  }: KeypadButtonProps): React.ReactElement => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const { isActive, pressHandlers } = usePressActive({ targetKey: digit, buttonRef });

    return (
      <button
        ref={buttonRef}
        onClick={() => onClick(digit)}
        {...pressHandlers}
        className={cn(
          'cursor-pointer',
          'flex flex-col items-center justify-center',
          'size-[56px] box-border rounded-full',
          'border-[1px] border-transparent',
          'transition-colors',
          'hover:bg-border',
          'focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-ring/50',
          isActive && 'bg-secondary-light active:bg-secondary-light'
        )}
        data-testid={testID}
        aria-label={`Digit ${digit}${label ? ` ${label}` : ''}`}
      >
        <span
          className={cn(
            'text-[16px] leading-[22px] font-semibold pointer-events-none',
            digit === '1' && '-mt-[15px]',
            isActive ? 'text-secondary-foreground' : 'text-foreground'
          )}
        >
          {digit}
        </span>
        {label && (
          <span
            className={cn(
              'text-[12px] leading-[16px] font-normal pointer-events-none',
              isActive ? 'text-secondary-foreground' : 'text-foreground'
            )}
          >
            {label}
          </span>
        )}
      </button>
    );
  }
);

KeypadButton.displayName = 'KeypadButton';
